import React, { useEffect, useState } from 'react';
import { graphql } from 'gatsby';
import styled from 'styled-components';
import _ from 'lodash';
import rehypeReact from 'rehype-react';

import Bio from '../components/bio';
import Layout from '../components/layout';
import SEO from '../components/seo';
import Post, { Node } from '../components/post';
import { rhythm } from '../utils/typography';
import { getReadDuration } from '../utils/read';
import { BadgeList } from '../components/badge';

import GraphCanvas from '../resources/graph-canvas';
import SortingCanvas from '../resources/sorting-canvas';
import ResponsiveTable from '../resources/responsive-table';
import CSV from '../resources/csv';

const renderAst = new rehypeReact({
  createElement: React.createElement,
  components: {
    'graph-canvas': GraphCanvas,
    'sorting-canvas': SortingCanvas,
    'responsive-table': ResponsiveTable,
    csv: CSV,
  },
}).Compiler;

type Data = {
  markdownRemark: Node;
};

type PageContext = {
  slug: string;
  previous?: Node;
  next?: Node;
  relatedPosts: {
    node: Node;
  }[];
};

type Props = {
  data: Data;
  pageContext: PageContext;
};

const Title = styled.h1`
  margin-top: ${rhythm(1)};
  margin-bottom: ${rhythm(1)};
`;

const Divider = styled.hr`
  margin-bottom: ${rhythm(1)};
`;

const RelatedPosts = styled.section`
  text-align: center;
`;

const Subtitle = styled.h2`
  margin-top: 0;
  margin-bottom: ${rhythm(1)};
`;

const BlogPostTemplate: React.FC<Props> = ({ data, pageContext }) => {
  const [duration, setDuration] = useState('');

  const {
    frontmatter: { title, description, date, tags },
    htmlAst,
    html,
    excerpt,
  } = data.markdownRemark;
  const { relatedPosts } = pageContext;

  useEffect(() => {
    setDuration(getReadDuration(document, html));
  }, []);

  return (
    <Layout>
      <SEO title={title} description={description || excerpt} />
      <article>
        <header>
          <Title>{title}</Title>
          <Bio
            hideSummary={true}
            date={date}
            readDuration={duration}
            gravatarSize={40}
          />
        </header>
        <section>{renderAst(htmlAst)}</section>
        <Divider />
        <footer></footer>
      </article>

      <RelatedPosts>
        <Subtitle>Explore more like this</Subtitle>
        {tags && (
          <BadgeList
            align="center"
            data={tags.map(tag => {
              return {
                to: `/tags/${_.kebabCase(tag)}`,
                text: tag,
              };
            })}
          />
        )}
        {relatedPosts &&
          relatedPosts.map(({ node }) => (
            <Post node={node} key={node.fields.slug} />
          ))}
      </RelatedPosts>
    </Layout>
  );
};

export default BlogPostTemplate;

export const pageQuery = graphql`
  query BlogPostBySlug($slug: String!) {
    markdownRemark(fields: { slug: { eq: $slug } }) {
      id
      excerpt(pruneLength: 160)
      htmlAst
      html
      frontmatter {
        title
        date(formatString: "MMMM DD, YYYY")
        description
        tags
      }
    }
  }
`;
